import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import {
  getStaleDevicePolicy,
  previewStaleDevicePolicy,
  updateStaleDevicePolicy,
  StaleDevicePolicy,
  StaleDeviceImpact,
} from '../services/api'

export function SettingsPage() {
  const { user, logout } = useAuth()
  const [apiUrl, setApiUrl] = useState(import.meta.env.VITE_API_URL || '')
  const [stale, setStale] = useState<StaleDevicePolicy | null>(null)
  const [impact, setImpact] = useState<StaleDeviceImpact | null>(null)
  const [staleHours, setStaleHours] = useState('24')
  const [graceHours, setGraceHours] = useState('')
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const [confirm, setConfirm] = useState<{ title: string; body: string; danger: boolean } | null>(null)

  useEffect(() => { loadStale() }, [])

  const HOUR = 3600 * 1000

  const fmt = (ms: number | null) =>
    ms === null ? '—' : new Date(ms).toLocaleString('zh-CN', { hour12: false })

  const loadStale = async () => {
    try {
      const res = await getStaleDevicePolicy()
      setStale(res.data)
      setStaleHours(String(Math.round(res.data.stale_ms / HOUR)))
      if (res.data.grace_until) {
        const remain = res.data.grace_until - Date.now()
        setGraceHours(remain > 0 ? String(Math.ceil(remain / HOUR)) : '0')
      } else {
        setGraceHours('')
      }
      const pv = await previewStaleDevicePolicy()
      setImpact(pv.data)
    } catch (e: any) {
      setNotice({ kind: 'err', text: '读取设置失败：' + (e?.message || '未知错误') })
    }
  }

  // 表单变化时按「保存后」的策略预估影响面
  const preview = async (override?: { stale_ms?: number; grace_until?: number | null }) => {
    try {
      const res = await previewStaleDevicePolicy(override)
      setImpact(res.data)
    } catch {
      // 预览失败不阻塞编辑
    }
  }

  const pendingPatch = () => {
    const patch: { stale_ms?: number; grace_until?: number | null } = {}
    const hours = Number(staleHours)
    if (Number.isFinite(hours) && hours > 0) patch.stale_ms = Math.round(hours * HOUR)
    if (graceHours.trim() === '') patch.grace_until = null
    else {
      const g = Number(graceHours)
      if (Number.isFinite(g) && g >= 0) patch.grace_until = Date.now() + g * HOUR
    }
    return patch
  }

  const save = async () => {
    setSaving(true)
    setNotice(null)
    try {
      const res = await updateStaleDevicePolicy(pendingPatch())
      setStale(res.data)
      setNotice({ kind: 'ok', text: '已保存，约 1 分钟内全量生效' })
      await preview()
    } catch (e: any) {
      setNotice({ kind: 'err', text: '保存失败：' + (e?.message || '未知错误') })
    } finally {
      setSaving(false)
    }
  }

  const requestSave = async () => {
    const patch = pendingPatch()
    // 保存前先看影响面：会永久删设备就必须二次确认
    const pv = await previewStaleDevicePolicy(patch)
    setImpact(pv.data)
    if (pv.data.will_delete > 0) {
      setConfirm({
        title: '确认保存？这会永久删除设备',
        body: `保存后 ${pv.data.will_delete} 台设备将立即从列表消失，并在每天 03:00 UTC（北京时间 11:00）的定时任务中被物理删除。删除不可恢复、无回收站，关联的上传文件也会一并清除。`,
        danger: true,
      })
    } else {
      setConfirm({ title: '确认保存？', body: '本次修改不会删除任何设备。', danger: false })
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Manage your account and preferences</p>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Account</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              value={user?.name || ''}
              readOnly
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="text"
              value={user?.email || ''}
              readOnly
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Role</label>
            <input
              type="text"
              value={user?.role || ''}
              readOnly
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-900"
            />
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">API Configuration</h2>
        <div className="space-y-4">
          <div>
            <label htmlFor="apiUrl" className="block text-sm font-medium text-gray-700">API Base URL</label>
            <input
              id="apiUrl"
              type="url"
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="https://api.eden.example.com"
            />
            <p className="mt-1 text-sm text-gray-500">Changes require application restart</p>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">已卸载设备自动清理</h2>
        <p className="text-sm text-gray-500 mb-4">
          Android 没有卸载回调，App 卸载后无法上报，只能按心跳超时判定。超过阈值的设备会从列表隐藏，并由每天 03:00 UTC（北京时间 11:00）的定时任务物理删除。
        </p>

        {stale && (
          <div className="mb-4 text-sm text-gray-600 bg-gray-50 rounded-md px-3 py-2">
            当前状态：{stale.in_grace ? '宽限期内（不隐藏、不删除）' : `已生效（${Math.round(stale.stale_ms / HOUR)} 小时无心跳即判定已卸载）`}
            {stale.in_grace && stale.grace_until && <>，宽限期至 {fmt(stale.grace_until)}</>}
            {impact && <>，当前共 {impact.total} 台设备</>}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label htmlFor="staleHours" className="block text-sm font-medium text-gray-700">心跳超时阈值（小时）</label>
            <input
              id="staleHours"
              type="number"
              min={1}
              value={staleHours}
              onChange={(e) => setStaleHours(e.target.value)}
              onBlur={() => {
                const h = Number(staleHours)
                if (Number.isFinite(h) && h > 0) preview({ stale_ms: Math.round(h * HOUR) })
              }}
              className="mt-1 block w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-sm text-gray-500">超过这个时长没有任何心跳，就认为 App 已被卸载（当前 24 小时）</p>
          </div>

          <div>
            <label htmlFor="graceHours" className="block text-sm font-medium text-gray-700">存量设备宽限期（小时）</label>
            <input
              id="graceHours"
              type="number"
              min={0}
              value={graceHours}
              placeholder="留空 = 不宽限"
              onChange={(e) => setGraceHours(e.target.value)}
              onBlur={() => {
                const g = Number(graceHours)
                if (graceHours.trim() !== '' && Number.isFinite(g) && g >= 0) {
                  preview({ grace_until: Date.now() + g * HOUR })
                } else if (graceHours.trim() === '') {
                  preview({ grace_until: null })
                }
              }}
              className="mt-1 block w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-sm text-gray-500">宽限期内不会隐藏也不会删除任何设备；留空或填 0 表示立即按阈值判定</p>
          </div>

          {impact && (
            <div className={`rounded-md px-3 py-2 text-sm ${impact.will_delete > 0 ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-600'}`}>
              影响面预览：共 {impact.total} 台，保存后列表显示 {impact.visible} 台
              {impact.hidden_now > 0 && <>，{impact.hidden_now} 台将被隐藏</>}
              {impact.will_delete > 0 && <>，并会在下次定时任务时被<b>永久删除</b></>}
              {impact.will_delete === 0 && <>，不会删除任何设备</>}
            </div>
          )}

          {notice && (
            <div className={`rounded-md px-3 py-2 text-sm ${notice.kind === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {notice.text}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={requestSave}
              disabled={saving || !stale}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? '保存中...' : '保存'}
            </button>
            <button
              onClick={loadStale}
              disabled={saving}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 disabled:opacity-50"
            >
              重新读取
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Danger Zone</h2>
        <button
          onClick={logout}
          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Sign Out
        </button>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className={`text-lg font-semibold ${confirm.danger ? 'text-red-700' : 'text-gray-900'}`}>
              {confirm.title}
            </h3>
            <p className="mt-2 text-sm text-gray-700 whitespace-pre-line">{confirm.body}</p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => setConfirm(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                取消
              </button>
              <button
                onClick={() => { setConfirm(null); save() }}
                className={`px-4 py-2 text-white rounded-md ${confirm.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                确认保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}